import type { MediaADFAttrs } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import { getMediaClient } from '@atlaskit/media-client-react';
import { setClientIdForFile } from '@atlaskit/media-common';
import {
	fetchSnippetMediaToken,
	parseSnippetContextId,
} from '@atlassian/editor-snippets-provider/snippetMediaClient';

type MediaNodeEntry = { contextId: string; node: ADFEntity; snippetId: string };
type SnippetToken = Awaited<ReturnType<typeof fetchSnippetMediaToken>>;
type UniqueMediaEntry = MediaNodeEntry & {
	fileId: string;
	sourceCollection: string;
};

type CopiedMediaAttrs = {
	__contextId: string | null;
	collection: string;
	id: string;
};

export type SnippetMediaCopyStatus = 'copied' | 'partial-copy' | 'failed' | 'copy-unavailable';

export type SnippetMediaCopyResult = {
	adf: ADFEntity[];
	failedMediaAttrs: MediaADFAttrs[];
	numFailedMediaFiles: number;
	numTotalMediaFiles: number;
	status: SnippetMediaCopyStatus;
};

export type CopySnippetMediaForInsertionOptions = {
	editorContextId?: string;
	mediaProvider?: MediaProvider;
};

type SnippetMediaCopyDestination = {
	collection: string;
	mediaClient: ReturnType<typeof getMediaClient>;
};

type CopySnippetMediaEntryResult =
	| {
			copiedAttrs: CopiedMediaAttrs;
			fileId: string;
			type: 'copied';
	  }
	| {
			attrs: MediaADFAttrs;
			type: 'failed';
	  };

/** Collects media/mediaInline nodes with a valid snippet __contextId. */
const collectMediaNode =
	(entries: MediaNodeEntry[]) =>
	(node: ADFEntity): undefined => {
		const contextId = node.attrs?.__contextId;
		if (contextId) {
			const parsed = parseSnippetContextId(contextId);
			if (parsed) {
				entries.push({ node, snippetId: parsed.snippetId, contextId });
			}
		}
	};

/** Fetches media tokens for all snippet IDs and returns them in a cache map. */
const fetchTokenCache = async (snippetIds: string[]): Promise<Map<string, SnippetToken>> => {
	const cache = new Map<string, SnippetToken>();
	const results = await Promise.allSettled(
		snippetIds.map(async (snippetId) => ({
			snippetId,
			token: await fetchSnippetMediaToken(snippetId),
		})),
	);
	for (const result of results) {
		if (result.status === 'fulfilled') {
			cache.set(result.value.snippetId, result.value.token);
		}
	}
	return cache;
};

/**
 * Returns true if the ADF body contains media nodes with a snippet __contextId.
 * Used to avoid the async insertion path when no snippet media copying is needed.
 */
export const hasSnippetMediaNodes = (adfBody: ADFEntity[]): boolean => {
	const entries: MediaNodeEntry[] = [];
	const handler = collectMediaNode(entries);
	const doc: ADFEntity = { type: 'doc', content: adfBody };
	traverse(doc, { media: handler, mediaInline: handler });
	return entries.length > 0;
};

const getUniqueMediaEntries = (adfBody: ADFEntity[]): UniqueMediaEntry[] => {
	const doc: ADFEntity = { type: 'doc', content: adfBody };
	const mediaNodes: MediaNodeEntry[] = [];
	const handler = collectMediaNode(mediaNodes);
	traverse(doc, { media: handler, mediaInline: handler });

	const seenNodes = new Set<string>();
	const uniqueEntries: UniqueMediaEntry[] = [];
	mediaNodes.forEach((entry) => {
		const fileId = entry.node.attrs?.id;
		if (!fileId || seenNodes.has(fileId)) {
			return;
		}
		seenNodes.add(fileId);
		uniqueEntries.push({
			...entry,
			fileId,
			sourceCollection: entry.node.attrs?.collection || `snippet-${entry.snippetId}`,
		});
	});

	return uniqueEntries;
};

export const getSnippetMediaAttrs = (adfBody: ADFEntity[]): MediaADFAttrs[] =>
	getUniqueMediaEntries(adfBody).map(({ node }) => node.attrs as MediaADFAttrs);

const isSnippetMediaAdfNode = (node: ADFEntity): boolean =>
	node.type === 'media' || node.type === 'mediaInline';

const rewriteCopiedMediaAttrs = (
	adfBody: ADFEntity[],
	copiedByFileId: Map<string, CopiedMediaAttrs>,
): ADFEntity[] => {
	const rewriteNode = (node: ADFEntity | undefined): ADFEntity | undefined => {
		if (!node) {
			return node;
		}

		const content = node.content?.map(rewriteNode).filter(Boolean) as ADFEntity[] | undefined;

		if (!isSnippetMediaAdfNode(node)) {
			return content ? { ...node, content } : node;
		}

		const fileId = node.attrs?.id;
		const copiedAttrs = fileId ? copiedByFileId.get(fileId) : undefined;

		if (!copiedAttrs) {
			return content ? { ...node, content } : node;
		}

		return {
			...node,
			...(content ? { content } : {}),
			attrs: {
				...node.attrs,
				id: copiedAttrs.id,
				collection: copiedAttrs.collection,
				__contextId: copiedAttrs.__contextId,
			},
		};
	};

	return adfBody.map(rewriteNode).filter(Boolean) as ADFEntity[];
};

const isCopiedMediaEntryResult = (
	result: CopySnippetMediaEntryResult,
): result is Extract<CopySnippetMediaEntryResult, { type: 'copied' }> => result.type === 'copied';

const isFailedMediaEntryResult = (
	result: CopySnippetMediaEntryResult,
): result is Extract<CopySnippetMediaEntryResult, { type: 'failed' }> => result.type === 'failed';

const getSnippetMediaCopyDestination = (
	mediaProvider?: MediaProvider,
): SnippetMediaCopyDestination | undefined => {
	const collection = mediaProvider?.uploadParams?.collection;
	const uploadMediaClientConfig = mediaProvider?.uploadMediaClientConfig;

	if (!collection || !uploadMediaClientConfig) {
		return;
	}

	return {
		collection,
		mediaClient: getMediaClient(uploadMediaClientConfig),
	};
};

const getCopiedMediaAttrsByFileId = (
	copyResults: CopySnippetMediaEntryResult[],
): Map<string, CopiedMediaAttrs> =>
	new Map(
		copyResults
			.filter(isCopiedMediaEntryResult)
			.map(({ fileId, copiedAttrs }) => [fileId, copiedAttrs]),
	);

const copySnippetMediaEntry = async ({
	copyDestination,
	editorContextId,
	entry,
	mediaClientBySnippetId,
	tokenCache,
}: {
	copyDestination: SnippetMediaCopyDestination;
	editorContextId?: string;
	entry: UniqueMediaEntry;
	mediaClientBySnippetId: Map<string, ReturnType<typeof getMediaClient>>;
	tokenCache: Map<string, SnippetToken>;
}): Promise<CopySnippetMediaEntryResult> => {
	try {
		const { fileId, snippetId, sourceCollection } = entry;
		const token = tokenCache.get(snippetId);

		if (!token) {
			throw new Error('Missing snippet media token');
		}

		let snippetMediaClient = mediaClientBySnippetId.get(snippetId);
		if (!snippetMediaClient) {
			snippetMediaClient = getMediaClient({
				authProvider: () =>
					Promise.resolve({
						clientId: token.clientId,
						token: token.token,
						baseUrl: token.baseUrl,
					}),
			});
			mediaClientBySnippetId.set(snippetId, snippetMediaClient);
		}

		setClientIdForFile(fileId, token.clientId);

		await snippetMediaClient.file.registerCopyIntent(fileId, sourceCollection);

		const copiedFile = await copyDestination.mediaClient.file.copyFile(
			{
				id: fileId,
				collection: sourceCollection,
				clientId: token.clientId,
			},
			{
				collection: copyDestination.collection,
			},
		);

		return {
			type: 'copied',
			fileId,
			copiedAttrs: {
				id: copiedFile.id,
				collection: copyDestination.collection,
				__contextId: editorContextId ?? null,
			},
		};
	} catch {
		return {
			type: 'failed',
			attrs: entry.node.attrs as MediaADFAttrs,
		};
	}
};

export const copySnippetMediaForInsertion = async (
	adfBody: ADFEntity[],
	{ mediaProvider, editorContextId }: CopySnippetMediaForInsertionOptions,
): Promise<SnippetMediaCopyResult> => {
	const uniqueEntries = getUniqueMediaEntries(adfBody);

	if (uniqueEntries.length === 0) {
		return {
			status: 'copied',
			adf: adfBody,
			numTotalMediaFiles: 0,
			numFailedMediaFiles: 0,
			failedMediaAttrs: [],
		};
	}

	const allMediaAttrs = uniqueEntries.map(({ node }) => node.attrs as MediaADFAttrs);
	const copyDestination = getSnippetMediaCopyDestination(mediaProvider);

	if (!copyDestination) {
		return {
			status: 'copy-unavailable',
			adf: adfBody,
			numTotalMediaFiles: uniqueEntries.length,
			numFailedMediaFiles: allMediaAttrs.length,
			failedMediaAttrs: allMediaAttrs,
		};
	}

	const snippetIds = [...new Set(uniqueEntries.map((entry) => entry.snippetId))];
	const tokenCache = await fetchTokenCache(snippetIds);
	const mediaClientBySnippetId = new Map<string, ReturnType<typeof getMediaClient>>();
	const copyResults = await Promise.all(
		uniqueEntries.map((entry) =>
			copySnippetMediaEntry({
				copyDestination,
				editorContextId,
				entry,
				mediaClientBySnippetId,
				tokenCache,
			}),
		),
	);

	const failedMediaAttrs = copyResults.filter(isFailedMediaEntryResult).map(({ attrs }) => attrs);

	if (failedMediaAttrs.length === uniqueEntries.length) {
		return {
			status: 'failed',
			adf: adfBody,
			numTotalMediaFiles: uniqueEntries.length,
			numFailedMediaFiles: failedMediaAttrs.length,
			failedMediaAttrs,
		};
	}

	return {
		status: failedMediaAttrs.length > 0 ? 'partial-copy' : 'copied',
		adf: rewriteCopiedMediaAttrs(adfBody, getCopiedMediaAttrsByFileId(copyResults)),
		numTotalMediaFiles: uniqueEntries.length,
		numFailedMediaFiles: failedMediaAttrs.length,
		failedMediaAttrs,
	};
};
